'use client';

import { c as _c } from "react/compiler-runtime";
import { DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator } from '@achra/ui/dropdown-menu';
import { Skeleton } from '@achra/ui/skeleton';
import { HardDrive } from 'lucide-react';
import Link from 'next/link';
import { useUserDrives } from '@/modules/shared/hooks/use-user-drives';
import { driveLinkFor } from '@/modules/shared/lib/switchboard-urls';
import type { Route } from 'next';
type MenuItemComponent = React.ComponentType<React.ComponentProps<typeof DropdownMenuItem>>;
type SeparatorComponent = React.ComponentType;
interface NavbarDrivesSectionProps {
  /** Menu-item component to render each drive with. Defaults to the desktop
   * `DropdownMenuItem`; the mobile menu passes `NavbarPrimitives.ActionOption`. */
  ItemComponent?: MenuItemComponent;
  /** Trailing separator component. Defaults to `DropdownMenuSeparator`; the mobile
   * menu passes `NavbarPrimitives.ActionOptionSeparator`. */
  Separator?: SeparatorComponent;
}

/**
 * Renders the authenticated user's drives as inline menu items, meant to sit between
 * "My Account" and "Log out" in the account dropdown. Reads from the shared
 * `useUserDrives` React Query cache (deduped across every navbar mount), so no extra
 * provider or store is needed.
 *
 * Shows a skeleton while loading and renders nothing (including its trailing separator)
 * when the user has no drives or the request errors.
 */
function NavbarDrivesSection(t0) {
  const $ = _c(10);
  const {
    ItemComponent: t1,
    Separator: t2
  } = t0;
  const ItemComponent = t1 === undefined ? DropdownMenuItem : t1;
  const Separator = t2 === undefined ? DropdownMenuSeparator : t2;
  const {
    data: drives,
    isPending,
    isError
  } = useUserDrives();
  if (isError) {
    return null;
  }
  if (!isPending && drives.length === 0) {
    return null;
  }
  let t3;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <DropdownMenuLabel className="text-muted-foreground text-xs font-normal uppercase">Apps</DropdownMenuLabel>;
    $[0] = t3;
  } else {
    t3 = $[0];
  }
  let t4;
  if ($[1] !== ItemComponent || $[2] !== drives || $[3] !== isPending) {
    t4 = isPending ? Array.from({
      length: 2
    }, _temp) : drives.map(drive => <ItemComponent key={drive.driveId} asChild={true} className="cursor-pointer"><Link href={driveLinkFor(drive.driveSlug) as Route} target="_blank" rel="noreferrer"><HardDrive /><span className="min-w-0 flex-1 truncate">{drive.driveName}</span></Link></ItemComponent>);
    $[1] = ItemComponent;
    $[2] = drives;
    $[3] = isPending;
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  let t5;
  if ($[5] !== Separator) {
    t5 = <Separator />;
    $[5] = Separator;
    $[6] = t5;
  } else {
    t5 = $[6];
  }
  let t6;
  if ($[7] !== t4 || $[8] !== t5) {
    t6 = <>{t3}{t4}{t5}</>;
    $[7] = t4;
    $[8] = t5;
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  return t6;
}
function _temp(_, index) {
  return <div key={index} className="flex items-center gap-2 px-2 py-1.5" aria-hidden={true}><HardDrive className="text-muted-foreground size-4 shrink-0" /><Skeleton className="h-4 w-full" /></div>;
}
export { NavbarDrivesSection };